import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { QRCodeSVG } from "qrcode.react";

import { auth, db, functions } from "../../shared/libs/firebaseConfig";

import "./CoursePaymentPage.css";

function formatBaht(value) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    maximumFractionDigits: 2,
  }).format(n);
}

function toDateText(ts) {
  try {
    if (!ts) return "-";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("th-TH");
  } catch {
    return "-";
  }
}

function getAllowedHeaderTypes(coursePackage) {
  switch (coursePackage) {
    case "F_2":
    case "P_2":
      return ["civil", "criminal", "summary_civil", "summary_criminal"];

    case "F_1_civil":
    case "P_1_civil":
      return ["civil", "summary_civil"];

    case "F_1_criminal":
    case "P_1_criminal":
      return ["criminal", "summary_criminal"];

    case "L_2":
      return ["civil", "criminal"];
    case "L_1_civil":
      return ["civil"];
    case "L_1_criminal":
      return ["criminal"];

    case "S_2":
      return ["summary_civil", "summary_criminal"];
    case "S_1_civil":
      return ["summary_civil"];
    case "S_1_criminal":
      return ["summary_criminal"];

    default:
      return [];
  }
}

function getFileExt(fileName = "") {
  const parts = String(fileName).split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "jpg";
}

function buildLearnUrl(courseId) {
  return `/learn/${courseId}`;
}

function addDays(baseDate, days) {
  const d = new Date(baseDate);
  d.setDate(d.getDate() + days);
  return d;
}

function toSafeDate(value) {
  try {
    if (!value) return null;
    if (value?.toDate) return value.toDate();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function formatThaiDateOnly(value) {
  try {
    const d = toSafeDate(value);
    if (!d) return "-";
    return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "-";
  }
}

function buildInstallmentsFromEnroll(enrollDoc, courseDoc) {
  const totalAmount = Number(enrollDoc?.amount || 0);
  const totalInstallments = Number(courseDoc?.maxInstallments || 0);
  const createdAt =
    toSafeDate(enrollDoc?.createdAt) ||
    toSafeDate(enrollDoc?.installmentPlan?.acceptedAt) ||
    new Date();

  if (!courseDoc?.allowInstallment || totalInstallments < 2 || totalAmount <= 0) {
    return [];
  }

  

  const baseAmount = Math.floor(totalAmount / totalInstallments);
  const remainder = totalAmount - (baseAmount * totalInstallments);

  return Array.from({ length: totalInstallments }, (_, index) => {
    const installmentNo = index + 1;
    const dueAt = addDays(createdAt, index * 30);

    return {
      installmentNo,
      amount:
        installmentNo === totalInstallments
          ? baseAmount + remainder
          : baseAmount,
      dueAt,
      status: "pending",
      paidAt: null,
      paymentChannel: "",
      paymentMethod: "",
      slipImagePath: "",
      qrDataPersonal: null,
      slipVerifiedData: null,
    };
  });
}

function getInstallmentList(enrollDoc, courseDoc) {
  const raw = Array.isArray(enrollDoc?.installments) ? enrollDoc.installments : [];
  if (raw.length > 0) {
    return raw.map((item, index) => ({
      installmentNo: Number(item?.installmentNo || index + 1),
      amount: Number(item?.amount || 0),
      dueAt: toSafeDate(item?.dueAt),
      status: item?.status || "pending",
      paidAt: item?.paidAt || null,
      paymentChannel: item?.paymentChannel || "",
      paymentMethod: item?.paymentMethod || "",
      slipImagePath: item?.slipImagePath || "",
      qrDataPersonal: item?.qrDataPersonal || null,
      slipVerifiedData: item?.slipVerifiedData || null,
    }));
  }

  return buildInstallmentsFromEnroll(enrollDoc, courseDoc);
}

function getCurrentInstallment(installments = []) {
  return installments.find((item) => item?.status !== "paid") || null;
}

function isInstallmentOverdue(installment) {
  if (!installment?.dueAt || installment?.status === "paid") return false;
  const due = toSafeDate(installment.dueAt);
  if (!due) return false;
  return Date.now() > due.getTime();
}

export default function CoursePaymentPromptpay() {
  const navigate = useNavigate();
  const { courseId, enrollId } = useParams();

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingQr, setLoadingQr] = useState(false);
  const [savingBasic, setSavingBasic] = useState(false);
  const [uploadingSlip, setUploadingSlip] = useState(false);
  const [verifyingSlip, setVerifyingSlip] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [course, setCourse] = useState(null);
  const [enroll, setEnroll] = useState(null);

  const [selectedPaymentType, setSelectedPaymentType] = useState("full");
  const [showInstallmentTermsModal, setShowInstallmentTermsModal] = useState(false);

  const [paymentMode, setPaymentMode] = useState("api");

  const [slipFile, setSlipFile] = useState(null);
  const [slipPreviewUrl, setSlipPreviewUrl] = useState("");
  const [uploadedSlipUrl, setUploadedSlipUrl] = useState("");

  const [qrData, setQrData] = useState({
    qrCode: "",
    accountName: "",
    amount: "",
  });

  const FACEBOOK_PAGE_URL = "https://web.facebook.com/lawlandd/about?_rdc=1&_rdr#";
  

  const allowInstallment = useMemo(() => {
    if (!course?.allowInstallment) return false;

    if (course?.courseType === "thaibar") {
      const keys = Array.isArray(course?.installmentPackageKeys)
        ? course.installmentPackageKeys
        : [];

      return !!enroll?.coursePackage && keys.includes(enroll.coursePackage);
    }

    return true;
  }, [course, enroll]);

  const canUseInstallmentUI = useMemo(() => {
    return !!allowInstallment || enroll?.paymentType === "installment";
  }, [allowInstallment, enroll]);

/* const effectivePaymentType = useMemo(() => {
  if (!allowInstallment) return "full";
  return enroll?.paymentType === "installment"
    ? "installment"
    : selectedPaymentType || "full";
}, [allowInstallment, enroll, selectedPaymentType]); */
const effectivePaymentType = useMemo(() => {
  // ถ้ารายการสมัครนี้เป็นผ่อนอยู่แล้ว ให้ถือเป็น installment เสมอ
  if (enroll?.paymentType === "installment") {
    return "installment";
  }

  // ถ้ายังไม่ได้เลือกผ่อน และคอร์สปิดโปรโมชั่นแล้ว ให้กลับเป็น full
  if (!allowInstallment) return "full";

  return selectedPaymentType || "full";
}, [allowInstallment, enroll, selectedPaymentType]);

const installmentList = useMemo(() => {
  return getInstallmentList(enroll, course);
}, [enroll, course]);

const currentInstallment = useMemo(() => {
  if (effectivePaymentType !== "installment") return null;
  return getCurrentInstallment(installmentList);
}, [effectivePaymentType, installmentList]);



const hasPaidAnyInstallment = useMemo(() => {
  return installmentList.some((item) => item?.status === "paid");
}, [installmentList]);

const currentInstallmentNo = useMemo(() => {
  return Number(currentInstallment?.installmentNo || 0);
}, [currentInstallment]);

const currentPayAmount = useMemo(() => {
  if (effectivePaymentType === "installment") {
    return Number(currentInstallment?.amount || 0);
  }
  return Number(enroll?.amount || 0);
}, [effectivePaymentType, currentInstallment, enroll]);

const amountText = useMemo(() => {
  return formatBaht(currentPayAmount || 0);
}, [currentPayAmount]);

const currentInstallmentOverdue = useMemo(() => {
  return isInstallmentOverdue(currentInstallment);
}, [currentInstallment]);

const installmentTermsPreview = useMemo(() => {
  if (!allowInstallment) return [];

  const previewItems =
    installmentList.length > 0
      ? installmentList
      : buildInstallmentsFromEnroll(enroll, course);

  return previewItems;
}, [allowInstallment, installmentList, enroll, course]);

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      setLoadingPage(true);
      setError("");

      try {
        if (!courseId || !enrollId) {
          throw new Error("ไม่พบข้อมูลการชำระเงิน");
        }

        const currentUser = auth.currentUser;
        if (!currentUser?.uid) {
          throw new Error("กรุณาเข้าสู่ระบบก่อน");
        }

        const enrollRef = doc(db, "enrolls", enrollId);
        const enrollSnap = await getDoc(enrollRef);

        if (!enrollSnap.exists()) {
          throw new Error("ไม่พบข้อมูลการสมัครเรียน");
        }

        // const enrollData = { id: enrollSnap.id, ...enrollSnap.data() };
        const enrollData = {
            id: enrollSnap.id,
            ...enrollSnap.data(),
            paymentType: enrollSnap.data()?.paymentType || "full",
        };

        if (String(enrollData.courseId || "") !== String(courseId || "")) {
          throw new Error("ข้อมูลคอร์สและการสมัครไม่ตรงกัน");
        }

        const courseRef = doc(db, "courses", courseId);
        const courseSnap = await getDoc(courseRef);

        if (!courseSnap.exists()) {
          throw new Error("ไม่พบข้อมูลคอร์ส");
        }

        const courseData = { id: courseSnap.id, ...courseSnap.data() };

        if (!mounted) return;

        setEnroll(enrollData);
        setCourse(courseData);

        const courseAllowInstallment = !!courseData?.allowInstallment;

        const packageAllowInstallment =
          courseData?.courseType === "thaibar"
            ? Array.isArray(courseData?.installmentPackageKeys) &&
              !!enrollData?.coursePackage &&
              courseData.installmentPackageKeys.includes(enrollData.coursePackage)
            : courseAllowInstallment;

        if (courseAllowInstallment && packageAllowInstallment) {
          setSelectedPaymentType(enrollData?.paymentType || "full");
        } else if (enrollData?.paymentType === "installment") {
          setSelectedPaymentType("installment");
        } else {
          setSelectedPaymentType("full");
        }

        // Stored QR fields may be legacy/client-written. Resolve through the backend.
        setQrData({ qrCode: "", accountName: "", amount: "" });

        setPaymentMode(courseData?.paymentApiEnabled === false ? "manual" : "api");
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setError(err?.message || "โหลดข้อมูลไม่สำเร็จ");
      } finally {
        if (mounted) setLoadingPage(false);
      }
    }

    loadData();

    return () => {
      mounted = false;
      if (slipPreviewUrl) {
        URL.revokeObjectURL(slipPreviewUrl);
      }
    };
  }, [courseId, enrollId]);

  useEffect(() => {
    let cancelled = false;
    async function ensureQr() {
        if (loadingPage) return;
        if (!enroll?.id || !course?.id) return;
        if (paymentMode !== "api") return;

        if (
            enroll?.paymentStatus === "paid" ||
            (effectivePaymentType === "installment" && !currentInstallment)
        ) {
            return;
        }

        if (qrData.qrCode) return;

        try {
            setLoadingQr(true);
            setError("");

            const createQrFn = httpsCallable(
            functions,
            "createEnrollPromptPayQRCodePersonal"
            );

            const payload = {
            enrollId: enroll.id,
            courseId: course.id,
            paymentType: effectivePaymentType,
            installmentNo: currentInstallmentNo,
            };

            const res = await createQrFn(payload);

            if (cancelled) return;
            const data = res?.data || {};
            if (data.alreadyPaid) {
              setEnroll((prev) => ({ ...prev, paymentStatus: data.paymentStatus,
                status: data.status, accessStatus: data.accessStatus }));
            }
            setQrData({
            qrCode: data?.qrCode || "",
            accountName: data?.accountName || "",
            amount: data?.amount || "",
            });
        } catch (err) {
            if (cancelled) return;
            console.error(err);
            setError(err?.message || "สร้าง QR PromptPay ไม่สำเร็จ");
        } finally {
            if (!cancelled) setLoadingQr(false);
        }
    }

    ensureQr();
    return () => { cancelled = true; };
  }, [
    loadingPage,
    enroll,
    course,
    paymentMode,
    qrData.qrCode,
    effectivePaymentType,
    currentPayAmount,
    currentInstallmentNo,
    currentInstallment,
  ]);

  /* const amountText = useMemo(() => {
    return formatBaht(enroll?.amount || 0);
  }, [enroll]); */


  const allowedHeaderTypes = useMemo(() => {
    return getAllowedHeaderTypes(enroll?.coursePackage || "");
  }, [enroll]);

  /* const canStartLearningNow = useMemo(() => {
    return (
      enroll?.paymentStatus === "paid" &&
      enroll?.status === "active" &&
      enroll?.accessStatus === "granted"
    );
  }, [enroll]); */
  const canStartLearningNow = useMemo(() => {
    const paymentStatus = enroll?.paymentStatus || "";
    const status = enroll?.status || "";
    const accessStatus = enroll?.accessStatus || "";

    const paymentOk =
        paymentStatus === "paid" || paymentStatus === "partial_paid";

    return paymentOk && status === "active" && accessStatus === "granted";
  }, [enroll]);

  const isBusy =
    loadingQr || savingBasic || uploadingSlip || verifyingSlip || redirecting;

  const onClickGoLearn = () => {
    if (!course?.id) return;
    navigate(buildLearnUrl(course.id));
  };

const onOpenInstallmentTerms = () => {
  setError("");
  setSuccessMsg("");

  if (!allowInstallment) {
    setError("แพ็กเกจนี้ยังไม่รองรับการผ่อนชำระ");
    return;
  }

  setShowInstallmentTermsModal(true);
};

const onCloseInstallmentTerms = () => {
  setShowInstallmentTermsModal(false);
};

const onSwitchBackToFullPayment = async () => {
  if (!enroll?.id) return;
  if (hasPaidAnyInstallment) {
    setError("ไม่สามารถเปลี่ยนกลับเป็นชำระเต็มได้ เนื่องจากมีการชำระงวดแล้ว");
    return;
  }

  try {
    setError("");
    setSuccessMsg("");

    await updateDoc(doc(db, "enrolls", enroll.id), {
      paymentType: "full",
      paymentStatus: "pending",
      status: "pending_payment",
      installmentPlan: null,
      installments: [],
      updatedAt: serverTimestamp(),
    });

    setEnroll((prev) => ({
      ...prev,
      paymentType: "full",
      paymentStatus: "pending",
      status: "pending_payment",
      installmentPlan: null,
      installments: [],
    }));

    setSelectedPaymentType("full");
    setQrData({
      qrCode: "",
      accountName: "",
      amount: "",
    });

    setSuccessMsg("เปลี่ยนกลับเป็นชำระเต็มจำนวนแล้ว");
  } catch (err) {
    console.error(err);
    setError(err?.message || "เปลี่ยนกลับเป็นชำระเต็มจำนวนไม่สำเร็จ");
  }
};

  const onSelectInstallmentPlan = async () => {
    if (!enroll?.id || !course?.id) return;
    if (!allowInstallment) {
      setError("แพ็กเกจนี้ยังไม่รองรับการผ่อนชำระ");
      return;
    }

    try {
        setError("");
        setSuccessMsg("");

        const generatedInstallments = buildInstallmentsFromEnroll(enroll, course);
        if (!generatedInstallments.length) {
        throw new Error("ไม่สามารถสร้างแผนผ่อนชำระได้");
        }

        await updateDoc(doc(db, "enrolls", enroll.id), {
        paymentType: "installment",
        paymentStatus: "pending_installment",
        status: "pending_payment",
        qrDataPersonal: null,
        installmentPlan: {
            enabled: true,
            acceptedAt: serverTimestamp(),
            totalAmount: Number(enroll?.amount || 0),
            totalInstallments: Number(course?.maxInstallments || 0),
            installmentAmount: Number(generatedInstallments?.[0]?.amount || 0),
            status: "ongoing",
        },
        installments: generatedInstallments,
        updatedAt: serverTimestamp(),
        });

        setEnroll((prev) => ({
        ...prev,
        paymentType: "installment",
        paymentStatus: "pending_installment",
        status: "pending_payment",
        installmentPlan: {
            enabled: true,
            totalAmount: Number(prev?.amount || 0),
            totalInstallments: Number(course?.maxInstallments || 0),
            installmentAmount: Number(generatedInstallments?.[0]?.amount || 0),
            status: "ongoing",
        },
        installments: generatedInstallments,
        }));

        setSelectedPaymentType("installment");
        setQrData({
        qrCode: "",
        accountName: "",
        amount: "",
        });

        setShowInstallmentTermsModal(false);
        setSuccessMsg("กรุณาชำระงวดปัจจุบัน");
    } catch (err) {
        console.error(err);
        setError(err?.message || "ตั้งค่าผ่อนชำระไม่สำเร็จ");
    }
  };

  const onRefreshQr = async () => {
    if (enroll?.paymentStatus === "paid" ||
        (effectivePaymentType === "installment" && !currentInstallment)) return;
    try {
      setError("");
      setSuccessMsg("");
      setLoadingQr(true);

      const createQrFn = httpsCallable(
        functions,
        "createEnrollPromptPayQRCodePersonal"
      );

      const res = await createQrFn({
        enrollId: enroll.id,
        courseId: course.id,
        forceRefresh: true,
        paymentType: effectivePaymentType,
        installmentNo: currentInstallmentNo,
      });

      const data = res?.data || {};
      setQrData({
        qrCode: data?.qrCode || "",
        accountName: data?.accountName || "",
        amount: data?.amount || "",
      });

      setSuccessMsg("รีเฟรช QR สำเร็จ");
    } catch (err) {
      console.error(err);
      setError(err?.message || "รีเฟรช QR ไม่สำเร็จ");
    } finally {
      setLoadingQr(false);
    }
  };

  const onSaveManualIntent = async () => {
    if (!enroll?.id) return;

    try {
      setSavingBasic(true);
      setError("");
      setSuccessMsg("");

      await updateDoc(doc(db, "enrolls", enroll.id), {
        paymentMethod: "manual_transfer",
        paymentChannel: "manual_qr",
        paymentStatus: "awaiting_admin_review",
        status: "pending_review",
        manualPaymentNoticeAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setEnroll((prev) => ({
        ...prev,
        paymentMethod: "manual_transfer",
        paymentChannel: "manual_qr",
        paymentStatus: "awaiting_admin_review",
        status: "pending_review",
      }));

      setSuccessMsg(
        "ระบบบันทึกข้อมูลการแจ้งชำระแบบพื้นฐานแล้ว กรุณาส่งหลักฐานไปที่เพจเพื่อรอแอดมินตรวจสอบ"
      );
    } catch (err) {
      console.error(err);
      setError(err?.message || "บันทึกข้อมูลไม่สำเร็จ");
    } finally {
      setSavingBasic(false);
    }
  };

  const ALLOWED_SLIP_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const MAX_SLIP_SIZE = 10 * 1024 * 1024; // 10MB

  const onSelectSlipFile = (e) => {
    const file = e.target.files?.[0] || null;

    setError("");
    setSlipFile(null);
    setUploadedSlipUrl("");

    if (slipPreviewUrl) {
      URL.revokeObjectURL(slipPreviewUrl);
      setSlipPreviewUrl("");
    }

    if (!file) return;

    if (!ALLOWED_SLIP_IMAGE_TYPES.includes(file.type)) {
      e.target.value = "";
      setError("กรุณาอัปโหลดเฉพาะไฟล์รูปภาพ JPG, PNG หรือ WEBP เท่านั้น");
      return;
    }

    if (file.size > MAX_SLIP_SIZE) {
      e.target.value = "";
      setError("ไฟล์สลิปมีขนาดใหญ่เกินไป กรุณาอัปโหลดไฟล์ไม่เกิน 10MB");
      return;
    }

    setSlipFile(file);
    setSlipPreviewUrl(URL.createObjectURL(file));
  };

  const uploadSlipToStorage = async ({ file, enrollDoc, courseDoc }) => {
    const currentUser = auth.currentUser;
    if (!currentUser?.uid) {
      throw new Error("กรุณาเข้าสู่ระบบก่อน");
    }
    if (!file) {
      throw new Error("กรุณาเลือกไฟล์สลิป");
    }
    if (!enrollDoc?.id || !courseDoc?.id) {
      throw new Error("ไม่พบข้อมูลที่จำเป็นสำหรับการอัปโหลด");
    }

    setUploadingSlip(true);

    try {
      const storage = getStorage();
      const ext = getFileExt(file.name);
      const storagePath = `payment-slips/${currentUser.uid}/${enrollDoc.id}/${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file, {
        contentType: file.type || "image/jpeg",
        customMetadata: {
          userId: currentUser.uid,
          enrollId: enrollDoc.id,
          courseId: courseDoc.id,
        },
      });

      const downloadURL = await getDownloadURL(storageRef);
      setUploadedSlipUrl(downloadURL);

      return {
        storagePath,
        downloadURL,
      };
    } finally {
      setUploadingSlip(false);
    }
  };

  /* const verifySlipViaCloudFunction = async ({
    enrollDoc,
    courseDoc,
    storagePath,
  }) => {
    setVerifyingSlip(true);

    try {
      const verifyFn = httpsCallable(
        functions,
        "verifyEnrollPaymentSlipPersonal"
      );

      const res = await verifyFn({
        enrollId: enrollDoc.id,
        courseId: courseDoc.id,
        storagePath,
        expectedAmount: enrollDoc.amount,
      });

      return res?.data || null;
    } finally {
      setVerifyingSlip(false);
    }
  }; */
  const verifySlipViaCloudFunction = async ({
    enrollDoc,
    courseDoc,
    storagePath,
    installmentNo = 0,
  }) => {
    setVerifyingSlip(true);

    try {
        const verifyFn = httpsCallable(
        functions,
        "verifyEnrollPaymentSlipPersonal"
        );

        const res = await verifyFn({
        enrollId: enrollDoc.id,
        courseId: courseDoc.id,
        storagePath,
            installmentNo,
        paymentType: effectivePaymentType,
        });

        return res?.data || null;
    } finally {
        setVerifyingSlip(false);
    }
  };

  const onVerifyByEasySlip = async (e) => {
    e.preventDefault();

    try {
      setError("");
      setSuccessMsg("");

      if (!enroll?.id || !course?.id) {
        throw new Error("ไม่พบข้อมูลการสมัครเรียน");
      }

      if (!slipFile) {
        throw new Error("กรุณาอัปโหลดหลักฐานการชำระเงิน");
      }

      const { storagePath, downloadURL } = await uploadSlipToStorage({
        file: slipFile,
        enrollDoc: enroll,
        courseDoc: course,
      });

      /* const result = await verifySlipViaCloudFunction({
        enrollDoc: enroll,
        courseDoc: course,
        storagePath,
      }); */
      const result = await verifySlipViaCloudFunction({
        enrollDoc: enroll,
        courseDoc: course,
        storagePath,
        installmentNo: currentInstallmentNo,
      });

      /* setEnroll((prev) => ({
        ...prev,
        paymentMethod: "slip2go",
        paymentChannel: "promptpay_qr_dynamic_personal",
        paymentStatus: result?.paymentStatus || "paid",
        status: result?.status || "active",
        accessStatus: result?.accessStatus || "granted",
        allowedHeaderTypes: result?.allowedHeaderTypes || allowedHeaderTypes,
        slipImagePath: storagePath,
        slipImageURL: downloadURL,
      })); */
      setEnroll((prev) => {
        const next = {
            ...prev,
            paymentMethod: "slip2go",
            paymentChannel:
            effectivePaymentType === "installment"
                ? "promptpay_qr_dynamic_personal_installment"
                : "promptpay_qr_dynamic_personal",
            paymentStatus:
            result?.paymentStatus ||
            (effectivePaymentType === "installment" ? "partial_paid" : "paid"),
            status: result?.status || "active",
            accessStatus: result?.accessStatus || "granted",
            allowedHeaderTypes: result?.allowedHeaderTypes || allowedHeaderTypes,
            slipImagePath: storagePath,
            slipImageURL: downloadURL,
        };

        if (effectivePaymentType !== "installment") {
            return next;
        }

        const items = getInstallmentList(prev, course).map((item) => {
            if (Number(item.installmentNo) !== Number(currentInstallmentNo)) return item;
            return {
            ...item,
            status: "paid",
            paidAt: new Date(),
            paymentChannel: "promptpay_qr_dynamic_personal_installment",
            paymentMethod: "slip2go",
            slipImagePath: storagePath,
            };
        });

        const hasUnpaid = items.some((item) => item.status !== "paid");

        return {
            ...next,
            installments: items,
            paymentType: "installment",
            paymentStatus: hasUnpaid ? "partial_paid" : "paid",
            status: "active",
            accessStatus: "granted",
        };
      });

    //   setSuccessMsg("ตรวจสอบการชำระเงินสำเร็จ ระบบกำลังพาไปหน้าเรียน...");
      setSuccessMsg(
        effectivePaymentType === "installment"
            ? `ตรวจสอบการชำระเงินงวดที่ ${currentInstallmentNo} สำเร็จ ระบบกำลังพาไปหน้าเรียน...`
            : "ตรวจสอบการชำระเงินสำเร็จ ระบบกำลังพาไปหน้าเรียน..."
      );
      setRedirecting(true);

      setTimeout(() => {
        navigate(buildLearnUrl(course.id));
      }, 800);
    } catch (err) {
      console.error(err);
      setError(err?.message || "ตรวจสอบสลิปไม่สำเร็จ");
    } finally {
      setRedirecting(false);
    }
  };

  if (loadingPage) {
    return (
      <div className="container py-4">
        <div className="d-flex align-items-center gap-2 text-secondary">
          <div className="spinner-border spinner-border-sm" role="status" />
          <span>กำลังโหลดข้อมูลการชำระเงิน...</span>
        </div>
      </div>
    );
  }

  if (error && !enroll) {
    return (
      <div className="container py-4">
        <Link to="/courses" className="text-decoration-none">
          ← กลับไปหน้าคอร์สทั้งหมด
        </Link>
        <div className="alert alert-danger mt-3 mb-0">{error}</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="mb-4">
        <h1 className="h4 mt-2 mb-1">ชำระเงินคอร์สเรียน</h1>
        <div className="text-secondary">
          {enroll?.courseTitle || course?.title || "-"}
        </div>
      </div>

      {/* {successMsg && <div className="alert alert-success">{successMsg}</div>}
      {error && <div className="alert alert-danger">{error}</div>} */}

      {/* {canStartLearningNow && (
        <div className="alert alert-success d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>การชำระเงินเสร็จสมบูรณ์แล้ว คุณสามารถเริ่มเรียนได้ทันที</div>
          <button className="btn btn-success" onClick={onClickGoLearn}>
            ไปหน้าเรียน
          </button>
        </div>
      )} */}
      {canStartLearningNow && (
        <div className="alert alert-success d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
            {effectivePaymentType === "installment" && enroll?.paymentStatus === "partial_paid"
                ? `คุณได้ชำระค่างวดแล้ว และสามารถเรียนต่อได้ตามสิทธิปัจจุบัน${
                    currentInstallment
                    ? ` • งวดถัดไปคือ งวดที่ ${currentInstallment.installmentNo} จำนวน ${formatBaht(
                        currentInstallment.amount
                        )}`
                    : ""
                }`
                : "การชำระเงินเสร็จสมบูรณ์แล้ว คุณสามารถเริ่มเรียนได้ทันที"}
            </div>

            <button className="btn btn-success" onClick={onClickGoLearn}>
            ไปหน้าเรียน
            </button>
        </div>
      )}

      <div className="row g-4 align-items-start">
        <div className="col-lg-7">
          <div className="card app-card course-payment-card">
            <div className="card-body p-4">
              <h2 className="h6 mb-3">ข้อมูลการสมัครเรียน</h2>

              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-secondary">คอร์ส</span>
                <span className="text-end">{enroll?.courseTitle || "-"}</span>
              </div>

              {!!enroll?.coursePackage && (
                <div className="d-flex justify-content-between gap-3 mb-2">
                  <span className="text-secondary">แพ็กเกจ</span>
                  <span className="text-end">
                    {enroll?.coursePackageLabel || enroll?.coursePackage}
                  </span>
                </div>
              )}

              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-secondary">ผู้สมัคร</span>
                <span className="text-end">
                  {enroll?.userSnapshot?.fullName || "-"}
                </span>
              </div>

              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-secondary">สถานะการสมัคร</span>
                <span className="text-end">{enroll?.status || "-"}</span>
              </div>

              <div className="d-flex justify-content-between gap-3 mb-2">
                <span className="text-secondary">สถานะการชำระเงิน</span>
                <span className="text-end">{enroll?.paymentStatus || "-"}</span>
              </div>

              <div className="d-flex justify-content-between gap-3">
                <span className="text-secondary">สร้างรายการเมื่อ</span>
                <span className="text-end">{toDateText(enroll?.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* {allowInstallment && ( */}
          {canUseInstallmentUI && (
            <div className="card app-card course-payment-card mb-4">
                <div className="card-body p-4">
                <h2 className="h6 mb-3">เลือกรูปแบบการชำระเงิน</h2>

                <div className="d-flex flex-wrap gap-2 mb-2">
                    <button
                        type="button"
                        className={`btn ${
                        effectivePaymentType === "full" ? "btn-primary" : "btn-outline-primary"
                        }`}
                        onClick={() => {
                        if (enroll?.paymentType === "installment") {
                            onSwitchBackToFullPayment();
                            return;
                        }
                        setSelectedPaymentType("full");
                        setQrData({
                            qrCode: "",
                            accountName: "",
                            amount: "",
                        });
                        }}
                        disabled={isBusy || hasPaidAnyInstallment}
                    >
                        ชำระเต็มจำนวน
                    </button>

                    <button
                        type="button"
                        className={`btn ${
                        effectivePaymentType === "installment"
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => {
                        if (enroll?.paymentType === "installment") {
                            setSelectedPaymentType("installment");
                            return;
                        }
                        onOpenInstallmentTerms();
                        }}
                        disabled={isBusy}
                    >
                        ผ่อนชำระ
                    </button>
                </div>

                <div className="text-secondary small">
                    คอร์สนี้รองรับการผ่อนชำระได้สูงสุด {course?.maxInstallments || 0} งวด
                    {hasPaidAnyInstallment ? (
                        <span className="d-block mt-1 text-warning">
                        มีการชำระงวดแล้ว จึงไม่สามารถเปลี่ยนกลับเป็นชำระเต็มได้
                        </span>
                    ) : (
                        <span className="d-block mt-1">
                        {/* หากยังไม่เคยชำระงวดแรก ผู้ใช้ยังสามารถเปลี่ยนกลับเป็นชำระเต็มจำนวนได้ */}
                        </span>
                    )}
                </div>

                {/* <div className="text-secondary small">
                    คอร์สนี้รองรับการผ่อนชำระได้สูงสุด {course?.maxInstallments || 0} งวด
                </div> */}

                {effectivePaymentType === "installment" && installmentList.length > 0 && (
                    <div className="mt-3">
                    <div className="fw-semibold mb-2">ตารางงวดชำระ</div>

                    <div className="d-flex flex-column gap-2">
                        {installmentList.map((item) => (
                     
                        <div
                            key={item.installmentNo}
                            className="d-flex justify-content-between align-items-center gap-3 rounded p-3"
                            style={{
                                border: item.status === "paid"
                                ? "1px solid rgba(25, 135, 84, 0.45)"
                                : "1px solid rgba(255,255,255,0.12)",
                                background:
                                item.status === "paid"
                                    ? "rgba(25, 135, 84, 0.08)"
                                    : "transparent",
                            }}
                        >
                            <div>
                            <div className="fw-semibold">งวดที่ {item.installmentNo}</div>
                            <div className="small text-secondary">
                                ครบกำหนด {formatThaiDateOnly(item.dueAt)}
                            </div>
                            </div>

                            <div className="text-end">
                            <div className="fw-semibold">{formatBaht(item.amount)}</div>
                            {/* <div className="small text-secondary">
                                {item.status === "paid" ? "ชำระแล้ว" : "รอชำระ"}
                            </div> */}
                            <div
                                className={`small ${
                                    item.status === "paid" ? "text-success" : "text-secondary"
                                }`}
                                >
                                {item.status === "paid" ? "ชำระแล้ว" : "รอชำระ"}
                            </div>
                            </div>
                        </div>
                        ))}
                    </div>
                    </div>
                )}
                </div>
            </div>
            )}

          {paymentMode === "api" && (
            <div className="card app-card course-payment-card">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <h2 className="h6 mb-0">ชำระผ่าน QR + ตรวจสลิปอัตโนมัติ</h2>
                  <span className="badge text-bg-success">พร้อมใช้งาน</span>
                </div>

                {/* <div className="alert course-payment-alert-light">
                  กรุณาสแกนชำระเงินด้วย QR ด้านล่างตามยอดของคอร์สนี้
                  แล้วอัปโหลดสลิปเพื่อให้ระบบตรวจสอบและเปิดสิทธิเข้าเรียนอัตโนมัติ
                </div> */}
                <div className="alert course-payment-alert-light">
                    {effectivePaymentType === "installment" && currentInstallment ? (
                        <>
                        กรุณาชำระเงินงวดที่ <strong>{currentInstallment.installmentNo}</strong>{" "}
                        จำนวน <strong>{formatBaht(currentInstallment.amount)}</strong>{" "}
                        ภายในวันที่ <strong>{formatThaiDateOnly(currentInstallment.dueAt)}</strong>
                        {/* {currentInstallmentOverdue ? (
                            <div className="text-danger mt-2">
                            งวดนี้เลยกำหนดชำระแล้ว กรุณาชำระเพื่อใช้งานต่อเนื่อง
                            </div>
                        ) : null} */}
                        </>
                    ) : (
                        <>
                        กรุณาสแกนชำระเงินด้วย QR ด้านล่างตามยอดของคอร์สนี้
                        แล้วอัปโหลดสลิปเพื่อให้ระบบตรวจสอบและเปิดสิทธิเข้าเรียนอัตโนมัติ
                        </>
                    )}
                </div>

                <div className="row g-4 align-items-start">
                  <div className="col-md-6">
                    <div className="course-payment-box rounded p-3 text-center">
                      <div className="fw-semibold mb-2">
                        QR PromptPay ตามยอดคอร์ส
                      </div>

                      <div className="d-flex justify-content-center">
                        <div
                          className="bg-white rounded p-3"
                          style={{ display: "inline-block" }}
                        >
                          {loadingQr ? (
                            <div
                              className="d-flex flex-column align-items-center justify-content-center"
                              style={{ width: 260, height: 260 }}
                            >
                              <div
                                className="spinner-border spinner-border-sm mb-2"
                                role="status"
                              />
                              <div className="small text-muted">
                                กำลังสร้าง QR...
                              </div>
                            </div>
                          ) : qrData.qrCode ? (
                            <QRCodeSVG
                              value={qrData.qrCode}
                              size={260}
                              includeMargin={true}
                            />
                          ) : (
                            <div
                              className="d-flex flex-column align-items-center justify-content-center text-secondary"
                              style={{ width: 260, height: 260 }}
                            >
                              <div className="mb-2">ยังไม่สามารถสร้าง QR ได้</div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={onRefreshQr}
                                disabled={isBusy}
                              >
                                ลองอีกครั้ง
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* {!!qrData.accountName && (
                        <div className="small text-secondary mt-3">
                          ชื่อบัญชี: {qrData.accountName}
                        </div>
                      )} */}

                      {/* <div className="mt-3">
                        <div className="small text-secondary">ยอดที่ต้องโอน</div>
                        <div className="fw-semibold fs-4">
                          {qrData.amount
                            ? formatBaht(qrData.amount)
                            : amountText}
                        </div>
                      </div> */}
                      <div className="mt-3">
                        <div className="small text-secondary">
                            {effectivePaymentType === "installment" && currentInstallment
                            ? `ยอดที่ต้องโอน (งวดที่ ${currentInstallment.installmentNo})`
                            : "ยอดที่ต้องโอน"}
                        </div>
                        <div className="fw-semibold fs-4">
                            {qrData.amount ? formatBaht(qrData.amount) : amountText}
                        </div>
                      </div>

                      <div className="d-grid gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={onRefreshQr}
                          disabled={isBusy}
                        >
                          {loadingQr ? "กำลังสร้าง QR..." : "รีเฟรช QR"}
                        </button>
                      </div>

                      {/* <div className="small text-success mt-2">
                        ชำระเงินเพื่อและอัพโหดลเข้าเรียนทันที
                      </div> */}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <form onSubmit={onVerifyByEasySlip}>
                      <div className="mb-3">
                        <label className="form-label">
                          อัปโหลดหลักฐานการชำระเงิน
                        </label>
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                          className="form-control"
                          onChange={onSelectSlipFile}
                          disabled={isBusy}
                        />
                        <div className="form-text">
                          รองรับไฟล์รูปภาพ เช่น jpg, jpeg, png, webp
                          <br />
                          
                          <span className="text-warning">
                            สลิปธนาคารกรุงเทพ (Bangkok Bank) กรุณารอประมาณ 5 นาทีหลังชำระเงิน ก่อนอัปโหลด
                          </span>
                          <br />
                          <span className="text-warning">
                            หากตรวจสอบไม่สำเร็จ ลูกค้าไม่ต้องอัปโหลดสลิปเดิมซ้ำ และติดต่อแอดมิน
                          </span>
                          <br />
                          <span>
                            กรุณาติดต่อ{" "}
                            <a
                              href={FACEBOOK_PAGE_URL}
                              target="_blank"
                              rel="noreferrer"
                              className="text-decoration-none"
                            >
                              แอดมิน
                            </a>
                          </span>
                        </div>
                      </div>

                      {slipPreviewUrl && (
                        <div className="mb-3">
                          <div className="small text-secondary mb-2">
                            ตัวอย่างไฟล์ที่เลือก
                          </div>
                          <img
                            src={slipPreviewUrl}
                            alt="slip preview"
                            className="img-fluid rounded border-theme"
                            style={{ maxHeight: 360 }}
                          />
                        </div>
                      )}

                      {uploadedSlipUrl && (
                        <div className="alert alert-light border small">
                          อัปโหลดสลิปแล้วเรียบร้อย
                        </div>
                      )}

                      <div className="d-grid">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isBusy || !qrData.qrCode}
                        >
                          {uploadingSlip ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              กำลังอัปโหลดสลิป...
                            </>
                          ) : verifyingSlip ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              กำลังตรวจสอบสลิป...
                            </>
                          ) : redirecting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              กำลังพาไปหน้าเรียน...
                            </>
                          ) : (
                            "ตรวจสอบสลิปและเริ่มเรียน"
                          )}
                        </button>
                      </div>
                      {/* 🔴 error ใต้ปุ่ม */}
                        {error && (
                        <div className="alert alert-danger mt-3 mb-0">
                            {error}
                        </div>
                        )}

                        {/* 🟢 success ใต้ error */}
                        {successMsg && (
                        <div className="alert alert-success mt-2 mb-0">
                            {successMsg}
                        </div>
                        )}

                      <div className="text-secondary small mt-2">
                        ตรวจสอบสลิปอัตโนมัติเพื่อเข้าเรียนได้ทันที
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {paymentMode === "manual" && (
            <div className="card app-card course-payment-card">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <h2 className="h6 mb-0">
                    วิธีพื้นฐาน (กรณีระบบตรวจสลิปมีปัญหา)
                  </h2>
                  <span className="badge text-bg-warning">สำรอง</span>
                </div>

                <div className="alert alert-warning">
                  ขอบคุณสำหรับการสมัครคอร์สเรียน{" "}
                  <strong>{enroll?.courseTitle || "-"}</strong> โปรดแจ้งการสมัครเรียนพร้อมส่งหลักฐานการโอนเงินได้ที่เพจ{" "}
                  <a
                    href={FACEBOOK_PAGE_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Facebook Page
                  </a>{" "}
                  ซึ่งแอดมินจะตรวจสอบข้อมูลการสมัครและผู้เรียนสามารถเข้าเรียนได้ภายใน 4 ชั่วโมง
                </div>

                <div className="course-payment-box rounded p-3">
                  <div className="fw-semibold mb-2">ขั้นตอน</div>
                  <ol className="mb-3">
                    <li>ติดต่อทีมงานที่เพจ</li>
                    <li>แจ้งชื่อผู้สมัครและชื่อคอร์ส</li>
                    <li>ส่งหลักฐานการโอนเงินให้แอดมิน</li>
                    <li>รอแอดมินอนุมัติสิทธิ์เข้าเรียน</li>
                  </ol>

                  <div className="d-grid gap-2">
                    <a
                      href={FACEBOOK_PAGE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-primary"
                    >
                      ไปที่เพจ Facebook
                    </a>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={onSaveManualIntent}
                      disabled={isBusy}
                    >
                      {savingBasic ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                          />
                          กำลังบันทึก...
                        </>
                      ) : (
                        "บันทึกว่าเลือกชำระแบบพื้นฐาน"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="col-lg-5">
          <div className="card app-card course-payment-card">
            <div className="card-body p-4">
              <div className="fw-semibold mb-3">สรุปยอดชำระ</div>

              <div className="d-flex justify-content-between gap-3">
                <span className="text-secondary">คอร์ส</span>
                <span className="text-end">{enroll?.courseTitle || "-"}</span>
              </div>

              {!!enroll?.coursePackage && (
                <div className="d-flex justify-content-between gap-3 mt-2">
                  <span className="text-secondary">แพ็กเกจ</span>
                  <span className="text-end">
                    {enroll?.coursePackageLabel || enroll?.coursePackage}
                  </span>
                </div>
              )}

              <hr />

              {/* <div className="d-flex justify-content-between align-items-center">
                <span className="text-secondary">ยอดรวม</span>
                <span className="fw-semibold fs-5">{amountText}</span>
              </div> */}
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-secondary">
                    {effectivePaymentType === "installment" && currentInstallment
                    ? `ยอดงวดที่ ${currentInstallment.installmentNo}`
                    : "ยอดรวม"}
                </span>
                <span className="fw-semibold fs-5">{amountText}</span>
                </div>

                {effectivePaymentType === "installment" && (
                <div className="text-secondary small mt-3">
                    ยอดรวมทั้งคอร์ส {formatBaht(enroll?.amount || 0)} •
                    ผ่อนสูงสุด {course?.maxInstallments || 0} งวด
                </div>
                )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showInstallmentTermsModal}
        onHide={onCloseInstallmentTerms}
        centered
      >
        <Modal.Header closeButton>
            <Modal.Title>ข้อกำหนดและเงื่อนไขการผ่อนชำระ</Modal.Title>
        </Modal.Header>

        <Modal.Body>
            <div className="mb-3">
            ผู้ใช้บริการตกลงที่จะผ่อนชำระ{" "}
            <strong>{course?.maxInstallments || 0} งวด</strong>{" "}
            และผู้ใช้บริการสามารถดูคอร์สเรียนต่อเนื่องได้
            หากได้ดำเนินการชำระเงินก่อนหรือถึงวันที่ถึงกำหนดในงวดนั้น ๆ
            โดยการทำรายการผ่อนชำระแต่ละงวดนี้
            ผู้ใช้บริการไม่ต้องเสียดอกเบี้ยแต่อย่างใด
            </div>

            <div className="d-flex flex-column gap-2">
            {installmentTermsPreview.map((item) => (
                <div
                key={`preview-${item.installmentNo}`}
                className="border rounded p-3"
                >
                <div className="fw-semibold">
                    งวดที่ {item.installmentNo}
                </div>
                <div className="small text-secondary">
                    วันที่ครบกำหนด {formatThaiDateOnly(item.dueAt)}
                </div>
                <div className="mt-1">
                    จำนวนเงิน {formatBaht(item.amount)}
                </div>
                </div>
            ))}
            </div>
        </Modal.Body>

        <Modal.Footer>
            <Button variant="outline-secondary" onClick={onCloseInstallmentTerms}>
            ยกเลิก
            </Button>
            <Button variant="primary" onClick={onSelectInstallmentPlan}>
            ยอมรับ
            </Button>
        </Modal.Footer>
        </Modal>
    </div>
  );
}